import React, { useEffect, useMemo, useState } from "react";

import {
  Plus,
  Search,
  RefreshCw,
  BriefcaseBusiness,
  MapPin,
  CalendarDays,
  IndianRupee,
  Users,
  Eye,
  Pencil,
  Trash2,
  Clock3,
  CheckCircle2,
  XCircle,
  FileText,
  Filter,
  MoreVertical,
  ChevronDown,
  AlertCircle,
  X,
  UserRound,
  Building2,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

function FreelanceProjects() {
  const navigate = useNavigate();

  // =========================================================
  // API
  // =========================================================

  const PROJECTS_API =
    "/api/freelance/projects";

  // =========================================================
  // STATE
  // =========================================================

  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openMenu, setOpenMenu] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // =========================================================
  // AUTH HEADERS
  // =========================================================

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      ...(token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {}),
    };
  };

  // =========================================================
  // FETCH PROJECTS
  // =========================================================

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(PROJECTS_API, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      const text = await response.text();

      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = text;
      }

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.error ||
            `Unable to fetch projects. HTTP ${response.status}`
        );
      }

      let result = [];

      if (Array.isArray(data)) {
        result = data;
      } else if (Array.isArray(data?.data)) {
        result = data.data;
      } else if (Array.isArray(data?.content)) {
        result = data.content;
      } else if (Array.isArray(data?.projects)) {
        result = data.projects;
      } else if (Array.isArray(data?.result)) {
        result = data.result;
      }

      setProjects(result);
    } catch (err) {
      console.error("FETCH FREELANCE PROJECTS ERROR:", err);

      setProjects([]);

      setError(
        err?.message || "Unable to fetch freelance projects."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    fetchProjects();
  }, []);

  // =========================================================
  // PROJECT ID
  // =========================================================

  const getProjectId = (project) => {
    return (
      project?.id ||
      project?.projectId ||
      project?.freelanceProjectId ||
      null
    );
  };

  // =========================================================
  // STATUS
  // =========================================================

  const getStatus = (project) => {
    return String(project?.status || "OPEN").toUpperCase();
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "OPEN":
        return "status-open";

      case "IN_PROGRESS":
        return "status-progress";

      case "COMPLETED":
        return "status-completed";

      case "CANCELLED":
        return "status-cancelled";

      case "DRAFT":
        return "status-draft";

      default:
        return "status-open";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "IN_PROGRESS":
        return "In Progress";

      case "COMPLETED":
        return "Completed";

      case "CANCELLED":
        return "Cancelled";

      case "DRAFT":
        return "Draft";

      case "OPEN":
        return "Open";

      default:
        return status || "Open";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "OPEN":
        return <CheckCircle2 size={12} />;

      case "IN_PROGRESS":
        return <Clock3 size={12} />;

      case "COMPLETED":
        return <CheckCircle2 size={12} />;

      case "CANCELLED":
        return <XCircle size={12} />;

      case "DRAFT":
        return <FileText size={12} />;

      default:
        return <FileText size={12} />;
    }
  };

  // =========================================================
  // TITLE
  // =========================================================

  const getTitle = (project) => {
    return (
      project?.title ||
      project?.projectTitle ||
      project?.name ||
      "Untitled Project"
    );
  };

  // =========================================================
  // DESCRIPTION
  // =========================================================

  const getDescription = (project) => {
    return (
      project?.description ||
      project?.projectDescription ||
      project?.details ||
      "No project description available."
    );
  };

  // =========================================================
  // ⭐ SKILLS NORMALIZER
  // Handles:
  // ["Java", "Spring Boot"]
  // "Java, Spring Boot"
  // "Java | Spring Boot"
  // [{name:"Java"}, {name:"Spring Boot"}]
  // ["Java, Spring Boot"]
  // =========================================================

  const getSkillsArray = (project) => {
    let skills =
      project?.skills ??
      project?.technology ??
      project?.technologies ??
      project?.requiredSkills ??
      project?.skillSet ??
      project?.technicalSkills ??
      [];

    // -------------------------------
    // NULL / EMPTY
    // -------------------------------

    if (
      skills === null ||
      skills === undefined ||
      skills === ""
    ) {
      return [];
    }

    // -------------------------------
    // ARRAY
    // -------------------------------

    if (Array.isArray(skills)) {
      const normalized = [];

      skills.forEach((item) => {
        // String item
        if (typeof item === "string") {
          item
            .split(",")
            .map((skill) => skill.trim())
            .filter(Boolean)
            .forEach((skill) => normalized.push(skill));

          return;
        }

        // Object item
        if (
          typeof item === "object" &&
          item !== null
        ) {
          const skillName =
            item?.name ||
            item?.skill ||
            item?.title ||
            item?.technology ||
            item?.label ||
            item?.value;

          if (skillName) {
            normalized.push(String(skillName).trim());
          }
        }
      });

      return [...new Set(normalized.filter(Boolean))];
    }

    // -------------------------------
    // OBJECT
    // -------------------------------

    if (
      typeof skills === "object" &&
      skills !== null
    ) {
      const skillName =
        skills?.name ||
        skills?.skill ||
        skills?.title ||
        skills?.technology ||
        skills?.label ||
        skills?.value;

      if (skillName) {
        return [String(skillName).trim()];
      }

      return [];
    }

    // -------------------------------
    // STRING
    // -------------------------------

    if (typeof skills === "string") {
      return skills
        .split(/[,|;\n]+/)
        .map((skill) => skill.trim())
        .filter(Boolean);
    }

    return [];
  };

  // =========================================================
  // LOCATION
  // =========================================================

  const getLocation = (project) => {
    return (
      project?.location ||
      project?.projectLocation ||
      "Remote"
    );
  };

  // =========================================================
  // PROJECT TYPE
  // =========================================================

  const getProjectType = (project) => {
    return (
      project?.projectType ||
      project?.employmentType ||
      project?.type ||
      "Fixed Price"
    );
  };

  // =========================================================
  // BUDGET
  // =========================================================

  const getBudget = (project) => {
    const min =
      project?.budgetMin ??
      project?.minBudget ??
      project?.salaryMin ??
      null;

    const max =
      project?.budgetMax ??
      project?.maxBudget ??
      project?.salaryMax ??
      null;

    if (min != null && max != null) {
      return `₹${Number(min).toLocaleString(
        "en-IN"
      )} - ₹${Number(max).toLocaleString(
        "en-IN"
      )}`;
    }

    if (min != null) {
      return `₹${Number(min).toLocaleString(
        "en-IN"
      )}+`;
    }

    if (max != null) {
      return `Up to ₹${Number(max).toLocaleString(
        "en-IN"
      )}`;
    }

    if (
      project?.budget !== undefined &&
      project?.budget !== null &&
      project?.budget !== ""
    ) {
      return String(project.budget);
    }

    return "Budget not specified";
  };

  // =========================================================
  // APPLICATION COUNT
  // =========================================================

  const getApplications = (project) => {
    if (
      project?.applicationsCount !== undefined &&
      project?.applicationsCount !== null
    ) {
      return project.applicationsCount;
    }

    if (
      project?.applicationCount !== undefined &&
      project?.applicationCount !== null
    ) {
      return project.applicationCount;
    }

    if (Array.isArray(project?.applications)) {
      return project.applications.length;
    }

    if (
      project?.applications !== undefined &&
      project?.applications !== null
    ) {
      return project.applications;
    }

    return 0;
  };

  // =========================================================
  // DEADLINE
  // =========================================================

  const getDeadline = (project) => {
    return (
      project?.deadline ||
      project?.endDate ||
      project?.projectDeadline ||
      project?.deadlineDate ||
      null
    );
  };

  // =========================================================
  // EXPERIENCE
  // =========================================================

  const getExperience = (project) => {
    return (
      project?.experienceRequired ||
      project?.experience ||
      project?.requiredExperience ||
      "Not specified"
    );
  };

  // =========================================================
  // DURATION
  // =========================================================

  const getDuration = (project) => {
    return (
      project?.duration ||
      project?.projectDuration ||
      "Not specified"
    );
  };

  // =========================================================
  // VACANCY
  // =========================================================

  const getVacancy = (project) => {
    return (
      project?.vacancy ??
      project?.vacancies ??
      project?.positions ??
      1
    );
  };

  // =========================================================
  // CLIENT NAME
  // =========================================================

  const getClientName = (project) => {
    return (
      project?.clientName ||
      project?.companyName ||
      project?.client ||
      "Client / Company"
    );
  };

  // =========================================================
  // CLIENT TYPE
  // =========================================================

  const getClientType = (project) => {
    return (
      project?.clientType ||
      project?.companyType ||
      "Company"
    );
  };

  // =========================================================
  // DATE FORMAT
  // =========================================================

  const formatDate = (date) => {
    if (!date) {
      return "Not specified";
    }

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return date;
    }

    return parsed.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // =========================================================
  // SEARCH + FILTER
  // =========================================================

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      const skills = getSkillsArray(project).join(" ");

      const text = `
        ${project?.title || ""}
        ${project?.projectTitle || ""}
        ${project?.description || ""}
        ${project?.projectDescription || ""}
        ${skills}
        ${project?.location || ""}
        ${project?.projectType || ""}
        ${project?.employmentType || ""}
        ${project?.experienceRequired || ""}
        ${project?.clientName || ""}
        ${project?.companyName || ""}
      `.toLowerCase();

      const searchValue =
        search.toLowerCase().trim();

      const matchesSearch =
        text.includes(searchValue);

      const projectStatus = String(
        project?.status || "OPEN"
      ).toUpperCase();

      const matchesStatus =
        statusFilter === "ALL" ||
        projectStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [
    projects,
    search,
    statusFilter,
  ]);

  // =========================================================
  // VIEW DETAILS
  // =========================================================

  const handleView = (project) => {
    setSelectedProject(project);
    setShowDetails(true);
    setOpenMenu(null);
  };

  // =========================================================
  // CLOSE DETAILS
  // =========================================================

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedProject(null);
  };

  // =========================================================
  // EDIT
  // =========================================================

  const handleEdit = (project) => {
    setOpenMenu(null);

    const projectId = getProjectId(project);

    if (!projectId) {
      alert("Project ID not found.");
      return;
    }

    navigate(
      `/partner/freelance-job/edit/${projectId}`
    );
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = async (project) => {
    const projectId = getProjectId(project);

    if (!projectId) {
      alert("Project ID not found.");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${getTitle(
        project
      )}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(projectId);

      const response = await fetch(
        `${PROJECTS_API}/${projectId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      const text = await response.text();

      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = text;
      }

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.error ||
            `Unable to delete project. HTTP ${response.status}`
        );
      }

      setProjects((prev) =>
        prev.filter(
          (item) =>
            getProjectId(item) !== projectId
        )
      );

      setOpenMenu(null);
    } catch (err) {
      console.error(
        "DELETE PROJECT ERROR:",
        err
      );

      alert(
        err?.message ||
          "Unable to delete project."
      );
    } finally {
      setDeletingId(null);
    }
  };

  // =========================================================
  // CREATE
  // =========================================================

  const handleCreateProject = () => {
    navigate("/partner/freelance-job");
  };

  // =========================================================
  // APPLICATIONS
  // =========================================================

  const handleApplications = (project) => {
    const projectId = getProjectId(project);

    if (!projectId) {
      alert("Project ID not found.");
      return;
    }

    navigate(
      `/partner/freelance-projects/${projectId}/applications`
    );
  };

  // =========================================================
  // STATISTICS
  // =========================================================

  const totalProjects = projects.length;

  const openProjects = projects.filter(
    (project) =>
      getStatus(project) === "OPEN"
  ).length;

  const inProgressProjects =
    projects.filter(
      (project) =>
        getStatus(project) ===
        "IN_PROGRESS"
    ).length;

  const completedProjects =
    projects.filter(
      (project) =>
        getStatus(project) ===
        "COMPLETED"
    ).length;

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="freelance-page">

      {/* HEADER */}

      <div className="freelance-header">

        <div className="header-left">

          <div className="header-icon">
            <BriefcaseBusiness size={22} />
          </div>

          <div>
            <h1>Freelance Projects</h1>

            <p>
              Manage, monitor and track all
              your posted freelance projects
            </p>
          </div>

        </div>

        <div className="header-actions">

          <button
            className="refresh-btn"
            onClick={fetchProjects}
            disabled={loading}
          >
            <RefreshCw
              size={15}
              className={
                loading ? "spin" : ""
              }
            />

            Refresh
          </button>

          <button
            className="post-project-btn"
            onClick={handleCreateProject}
          >
            <Plus size={16} />

            Post New Project
          </button>

        </div>

      </div>

      {/* STATISTICS */}

      <div className="stats-grid">

        <div className="stat-card">
          <div className="stat-icon blue">
            <BriefcaseBusiness size={18} />
          </div>

          <div>
            <span>Total Projects</span>
            <strong>{totalProjects}</strong>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <CheckCircle2 size={18} />
          </div>

          <div>
            <span>Open Projects</span>
            <strong>{openProjects}</strong>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon orange">
            <Clock3 size={18} />
          </div>

          <div>
            <span>In Progress</span>
            <strong>{inProgressProjects}</strong>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <CheckCircle2 size={18} />
          </div>

          <div>
            <span>Completed</span>
            <strong>{completedProjects}</strong>
          </div>
        </div>

      </div>

      {/* TOOLBAR */}

      <div className="project-toolbar">

        <div className="search-container">

          <Search size={17} />

          <input
            type="text"
            placeholder="Search projects, technology, location..."
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
          />

          {search && (
            <button
              className="clear-btn"
              onClick={() => setSearch("")}
            >
              ×
            </button>
          )}

        </div>

        <div className="filter-container">

          <Filter size={15} />

          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                e.target.value
              )
            }
          >
            <option value="ALL">
              All Status
            </option>

            <option value="OPEN">
              Open
            </option>

            <option value="IN_PROGRESS">
              In Progress
            </option>

            <option value="COMPLETED">
              Completed
            </option>

            <option value="CANCELLED">
              Cancelled
            </option>

            <option value="DRAFT">
              Draft
            </option>
          </select>

          <ChevronDown size={14} />

        </div>

      </div>

      {/* ERROR */}

      {error && (
        <div className="error-box">

          <div>
            <AlertCircle size={17} />
            <span>{error}</span>
          </div>

          <button onClick={fetchProjects}>
            Try Again
          </button>

        </div>
      )}

      {/* RESULT */}

      {!loading && (
        <div className="result-bar">

          <div>
            Showing{" "}
            <strong>
              {filteredProjects.length}
            </strong>{" "}
            of{" "}
            <strong>
              {totalProjects}
            </strong>{" "}
            projects
          </div>

          {(search ||
            statusFilter !== "ALL") && (
            <button
              onClick={() => {
                setSearch("");
                setStatusFilter("ALL");
              }}
            >
              Clear Filters
            </button>
          )}

        </div>
      )}

      {/* LOADING */}

      {loading ? (

        <div className="loading-state">

          <RefreshCw
            size={30}
            className="spin"
          />

          <h3>
            Loading Projects...
          </h3>

          <p>
            Fetching your freelance
            projects from server.
          </p>

        </div>

      ) : filteredProjects.length === 0 ? (

        <div className="empty-state">

          <div className="empty-icon">
            <FileText size={34} />
          </div>

          <h3>
            No Freelance Projects Found
          </h3>

          <p>
            {search ||
            statusFilter !== "ALL"
              ? "No projects match your current search or filter."
              : "No freelance projects are available from the server."}
          </p>

          {search ||
          statusFilter !== "ALL" ? (

            <button
              onClick={() => {
                setSearch("");
                setStatusFilter("ALL");
              }}
            >
              Clear Filters
            </button>

          ) : (

            <button
              onClick={handleCreateProject}
            >
              <Plus size={15} />
              Post Your First Project
            </button>

          )}

        </div>

      ) : (

        <div className="project-grid">

          {filteredProjects.map(
            (project) => {

              const projectId =
                getProjectId(project);

              const status =
                getStatus(project);

              const skills =
                getSkillsArray(project);

              return (

                <div
                  className="project-card"
                  key={
                    projectId ||
                    `project-${Math.random()}`
                  }
                >

                  {/* CARD HEADER */}

                  <div className="card-header">

                    <div className="project-icon">
                      <BriefcaseBusiness
                        size={19}
                      />
                    </div>

                    <div className="project-title-area">

                      <h3>
                        {getTitle(project)}
                      </h3>

                      <span>
                        Project ID:{" "}
                        {projectId || "-"}
                      </span>

                    </div>

                    <div className="menu-wrapper">

                      <button
                        className="menu-btn"
                        onClick={() =>
                          setOpenMenu(
                            openMenu ===
                              projectId
                              ? null
                              : projectId
                          )
                        }
                      >
                        <MoreVertical
                          size={17}
                        />
                      </button>

                      {openMenu ===
                        projectId && (

                        <div className="dropdown-menu">

                          <button
                            onClick={() =>
                              handleView(
                                project
                              )
                            }
                          >
                            <Eye size={14} />
                            View Details
                          </button>

                          <button
                            onClick={() =>
                              handleEdit(
                                project
                              )
                            }
                          >
                            <Pencil size={14} />
                            Edit Project
                          </button>

                          <button
                            className="delete-option"
                            disabled={
                              deletingId ===
                              projectId
                            }
                            onClick={() =>
                              handleDelete(
                                project
                              )
                            }
                          >
                            <Trash2
                              size={14}
                            />

                            {deletingId ===
                            projectId
                              ? "Deleting..."
                              : "Delete Project"}
                          </button>

                        </div>

                      )}

                    </div>

                  </div>

                  {/* STATUS */}

                  <div className="status-row">

                    <span
                      className={`project-status ${getStatusClass(
                        status
                      )}`}
                    >
                      {getStatusIcon(status)}

                      {getStatusLabel(status)}
                    </span>

                    <span className="project-type-badge">
                      {getProjectType(project)}
                    </span>

                  </div>

                  {/* DESCRIPTION */}

                  <p className="project-description">
                    {getDescription(project)}
                  </p>

                  {/* ⭐ SKILLS */}

                  <div className="technology-box">

                    <span>
                      TECHNOLOGY / SKILLS
                    </span>

                    {skills.length > 0 ? (

                      <div className="skills-list">

                        {skills.map(
                          (skill, index) => (
                            <span
                              className="skill-badge"
                              key={`${skill}-${index}`}
                            >
                              {skill}
                            </span>
                          )
                        )}

                      </div>

                    ) : (

                      <strong className="no-skills">
                        Not specified
                      </strong>

                    )}

                  </div>

                  {/* DETAILS */}

                  <div className="project-details">

                    <div className="project-detail">

                      <MapPin size={15} />

                      <div>
                        <span>
                          Location
                        </span>

                        <strong>
                          {getLocation(project)}
                        </strong>
                      </div>

                    </div>

                    <div className="project-detail">

                      <IndianRupee size={15} />

                      <div>
                        <span>
                          Budget
                        </span>

                        <strong>
                          {getBudget(project)}
                        </strong>
                      </div>

                    </div>

                    <div className="project-detail">

                      <CalendarDays size={15} />

                      <div>
                        <span>
                          Deadline
                        </span>

                        <strong>
                          {formatDate(
                            getDeadline(project)
                          )}
                        </strong>
                      </div>

                    </div>

                    <div className="project-detail">

                      <Users size={15} />

                      <div>
                        <span>
                          Applications
                        </span>

                        <strong>
                          {getApplications(project)}
                        </strong>
                      </div>

                    </div>

                  </div>

                  {/* EXTRA INFO */}

                  <div className="extra-info">

                    <div>
                      <span>
                        Experience
                      </span>

                      <strong>
                        {getExperience(project)}
                      </strong>
                    </div>

                    <div>
                      <span>
                        Duration
                      </span>

                      <strong>
                        {getDuration(project)}
                      </strong>
                    </div>

                    <div>
                      <span>
                        Vacancy
                      </span>

                      <strong>
                        {getVacancy(project)}
                      </strong>
                    </div>

                  </div>

                  {/* FOOTER */}

                  <div className="card-footer">

                    <button
                      className="view-btn"
                      onClick={() =>
                        handleView(project)
                      }
                    >
                      <Eye size={14} />
                      View Details
                    </button>

                    <button
                      className="applications-btn"
                      onClick={() =>
                        handleApplications(
                          project
                        )
                      }
                    >
                      <Users size={14} />

                      Applications (
                      {getApplications(project)}
                      )
                    </button>

                  </div>

                </div>
              );
            }
          )}

        </div>
      )}

      {/* =====================================================
          DETAILS MODAL
      ===================================================== */}

      {showDetails &&
        selectedProject && (

        <div
          className="modal-overlay"
          onClick={closeDetails}
        >

          <div
            className="project-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* MODAL HEADER */}

            <div className="modal-header">

              <div className="modal-title">

                <div className="modal-icon">
                  <BriefcaseBusiness
                    size={20}
                  />
                </div>

                <div>

                  <h2>
                    {getTitle(
                      selectedProject
                    )}
                  </h2>

                  <span>
                    Project ID:{" "}
                    {getProjectId(
                      selectedProject
                    ) || "-"}
                  </span>

                </div>

              </div>

              <button
                className="modal-close"
                onClick={closeDetails}
              >
                <X size={19} />
              </button>

            </div>

            {/* MODAL BODY */}

            <div className="modal-body">

              {/* STATUS */}

              <div className="modal-status-row">

                <span
                  className={`project-status ${getStatusClass(
                    getStatus(
                      selectedProject
                    )
                  )}`}
                >
                  {getStatusIcon(
                    getStatus(
                      selectedProject
                    )
                  )}

                  {getStatusLabel(
                    getStatus(
                      selectedProject
                    )
                  )}
                </span>

                <span className="project-type-badge">
                  {getProjectType(
                    selectedProject
                  )}
                </span>

              </div>

              {/* DESCRIPTION */}

              <div className="modal-section">

                <h4>
                  Project Description
                </h4>

                <p>
                  {getDescription(
                    selectedProject
                  )}
                </p>

              </div>

              {/* ⭐ REQUIRED TECHNOLOGY */}

              <div className="modal-section">

                <h4>
                  Required Technology
                </h4>

                {getSkillsArray(
                  selectedProject
                ).length > 0 ? (

                  <div className="modal-skills">

                    {getSkillsArray(
                      selectedProject
                    ).map(
                      (skill, index) => (
                        <span
                          className="modal-skill-badge"
                          key={`${skill}-${index}`}
                        >
                          {skill}
                        </span>
                      )
                    )}

                  </div>

                ) : (

                  <div className="modal-tech">
                    Not specified
                  </div>

                )}

              </div>

              {/* INFORMATION GRID */}

              <div className="modal-info-grid">

                <div className="modal-info-item">

                  <MapPin size={16} />

                  <div>
                    <span>
                      Location
                    </span>

                    <strong>
                      {getLocation(
                        selectedProject
                      )}
                    </strong>
                  </div>

                </div>

                <div className="modal-info-item">

                  <IndianRupee size={16} />

                  <div>
                    <span>
                      Budget
                    </span>

                    <strong>
                      {getBudget(
                        selectedProject
                      )}
                    </strong>
                  </div>

                </div>

                <div className="modal-info-item">

                  <CalendarDays size={16} />

                  <div>
                    <span>
                      Deadline
                    </span>

                    <strong>
                      {formatDate(
                        getDeadline(
                          selectedProject
                        )
                      )}
                    </strong>
                  </div>

                </div>

                <div className="modal-info-item">

                  <Users size={16} />

                  <div>
                    <span>
                      Applications
                    </span>

                    <strong>
                      {getApplications(
                        selectedProject
                      )}
                    </strong>
                  </div>

                </div>

                <div className="modal-info-item">

                  <Clock3 size={16} />

                  <div>
                    <span>
                      Duration
                    </span>

                    <strong>
                      {getDuration(
                        selectedProject
                      )}
                    </strong>
                  </div>

                </div>

                <div className="modal-info-item">

                  <UserRound size={16} />

                  <div>
                    <span>
                      Experience
                    </span>

                    <strong>
                      {getExperience(
                        selectedProject
                      )}
                    </strong>
                  </div>

                </div>

                <div className="modal-info-item">

                  <Users size={16} />

                  <div>
                    <span>
                      Vacancy
                    </span>

                    <strong>
                      {getVacancy(
                        selectedProject
                      )}
                    </strong>
                  </div>

                </div>

                <div className="modal-info-item">

                  <BriefcaseBusiness
                    size={16}
                  />

                  <div>
                    <span>
                      Project Type
                    </span>

                    <strong>
                      {getProjectType(
                        selectedProject
                      )}
                    </strong>
                  </div>

                </div>

              </div>

              {/* CLIENT */}

              <div className="client-box">

                <div className="client-icon">

                  <Building2 size={17} />

                </div>

                <div>

                  <span>
                    Posted By
                  </span>

                  <strong>
                    {getClientName(
                      selectedProject
                    )}
                  </strong>

                  <small>
                    {getClientType(
                      selectedProject
                    )}
                  </small>

                </div>

              </div>

            </div>

            {/* MODAL FOOTER */}

            <div className="modal-footer">

              <button
                className="modal-secondary-btn"
                onClick={closeDetails}
              >
                Close
              </button>

              <button
                className="modal-primary-btn"
                onClick={() =>
                  handleApplications(
                    selectedProject
                  )
                }
              >
                <Users size={15} />

                View Applications (
                {getApplications(
                  selectedProject
                )}
                )
              </button>

            </div>

          </div>

        </div>
      )}

      <style>
        {freelanceProjectsCSS}
      </style>

    </div>
  );
}

// =============================================================
// CSS
// =============================================================

const freelanceProjectsCSS = `

.freelance-page {
  min-height: 100vh;
  width: 100%;
  padding: 26px;
  box-sizing: border-box;

  background:
    linear-gradient(
      135deg,
      #f8fafc 0%,
      #eef4ff 100%
    );

  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
}

/* HEADER */

.freelance-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 13px;
}

.header-icon {
  width: 45px;
  height: 45px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  border-radius: 11px;

  background: #2563eb;
  color: #ffffff;

  box-shadow:
    0 5px 16px
    rgba(37, 99, 235, .20);
}

.freelance-header h1 {
  margin: 0;
  color: #0f172a;
  font-size: 23px;
  font-weight: 750;
  line-height: 1.2;
}

.freelance-header p {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 9px;
}

/* BUTTONS */

.refresh-btn,
.post-project-btn {
  height: 36px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  padding: 0 13px;

  border-radius: 7px;

  font-size: 11px;
  font-weight: 700;

  cursor: pointer;

  transition: .2s ease;
}

.refresh-btn {
  border: 1px solid #dbe3ef;
  background: #ffffff;
  color: #2563eb;
}

.refresh-btn:hover {
  background: #eff6ff;
}

.refresh-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.post-project-btn {
  border: none;
  background: #2563eb;
  color: #ffffff;

  box-shadow:
    0 4px 12px
    rgba(37, 99, 235, .18);
}

.post-project-btn:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

/* STATS */

.stats-grid {
  display: grid;

  grid-template-columns:
    repeat(4, minmax(0, 1fr));

  gap: 14px;

  margin-bottom: 20px;
}

.stat-card {
  display: flex;
  align-items: center;

  gap: 11px;

  padding: 15px;

  background: #ffffff;

  border: 1px solid #e2e8f0;

  border-radius: 11px;

  box-shadow:
    0 3px 13px
    rgba(15, 23, 42, .045);
}

.stat-icon {
  width: 38px;
  height: 38px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 9px;
}

.stat-icon.blue {
  background: #eff6ff;
  color: #2563eb;
}

.stat-icon.green {
  background: #f0fdf4;
  color: #16a34a;
}

.stat-icon.orange {
  background: #fff7ed;
  color: #ea580c;
}

.stat-icon.purple {
  background: #faf5ff;
  color: #9333ea;
}

.stat-card span {
  display: block;
  margin-bottom: 3px;
  color: #94a3b8;
  font-size: 9px;
}

.stat-card strong {
  color: #1e293b;
  font-size: 18px;
}

/* TOOLBAR */

.project-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;
  margin-bottom: 14px;
}

.search-container {
  max-width: 620px;

  flex: 1;

  height: 42px;

  display: flex;
  align-items: center;

  gap: 8px;

  padding: 0 12px;

  box-sizing: border-box;

  background: #ffffff;

  border: 1px solid #dbe3ef;

  border-radius: 8px;

  color: #94a3b8;
}

.search-container:focus-within {
  border-color: #60a5fa;

  box-shadow:
    0 0 0 3px
    rgba(37, 99, 235, .08);
}

.search-container input {
  width: 100%;
  height: 100%;

  border: none;
  outline: none;

  background: transparent;

  color: #1e293b;

  font-size: 11px;
}

.search-container input::placeholder {
  color: #94a3b8;
}

.clear-btn {
  width: 23px;
  height: 23px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: none;

  border-radius: 50%;

  background: #f1f5f9;

  color: #64748b;

  font-size: 16px;

  cursor: pointer;
}

.filter-container {
  height: 42px;

  display: flex;
  align-items: center;

  gap: 7px;

  padding: 0 10px;

  background: #ffffff;

  border: 1px solid #dbe3ef;

  border-radius: 8px;

  color: #64748b;
}

.filter-container select {
  min-width: 125px;

  border: none;
  outline: none;

  background: transparent;

  color: #475569;

  font-size: 10px;

  cursor: pointer;

  appearance: none;
}

/* RESULT */

.result-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 14px;

  color: #64748b;

  font-size: 10px;
}

.result-bar strong {
  color: #2563eb;
}

.result-bar button {
  border: none;

  background: transparent;

  color: #2563eb;

  font-size: 10px;
  font-weight: 700;

  cursor: pointer;
}

/* ERROR */

.error-box {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 15px;

  margin-bottom: 15px;

  padding: 11px 13px;

  background: #fef2f2;

  border: 1px solid #fecaca;

  border-radius: 8px;

  color: #b91c1c;

  font-size: 11px;
}

.error-box > div {
  display: flex;
  align-items: center;
  gap: 7px;
}

.error-box button {
  border: none;
  background: transparent;

  color: #2563eb;

  font-size: 10px;
  font-weight: 700;

  cursor: pointer;
}

/* PROJECT GRID */

.project-grid {
  display: grid;

  grid-template-columns:
    repeat(3, minmax(0, 1fr));

  gap: 16px;
}

/* CARD */

.project-card {
  min-width: 0;

  display: flex;
  flex-direction: column;

  padding: 16px;

  background: #ffffff;

  border: 1px solid #e2e8f0;

  border-radius: 13px;

  box-shadow:
    0 4px 15px
    rgba(15, 23, 42, .055);

  transition:
    transform .2s ease,
    box-shadow .2s ease,
    border-color .2s ease;
}

.project-card:hover {
  transform: translateY(-2px);

  border-color: #bfdbfe;

  box-shadow:
    0 9px 24px
    rgba(37, 99, 235, .09);
}

/* CARD HEADER */

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.project-icon {
  width: 38px;
  height: 38px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 9px;

  background: #eff6ff;

  color: #2563eb;
}

.project-title-area {
  min-width: 0;
  flex: 1;
}

.project-title-area h3 {
  margin: 0;

  color: #1e293b;

  font-size: 13px;
  font-weight: 750;

  line-height: 1.4;
}

.project-title-area span {
  display: block;

  margin-top: 3px;

  color: #94a3b8;

  font-size: 8px;
}

.menu-wrapper {
  position: relative;
}

.menu-btn {
  width: 29px;
  height: 29px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: none;

  border-radius: 6px;

  background: transparent;

  color: #94a3b8;

  cursor: pointer;
}

.menu-btn:hover {
  background: #f1f5f9;
  color: #475569;
}

/* DROPDOWN */

.dropdown-menu {
  position: absolute;

  z-index: 100;

  right: 0;
  top: 33px;

  width: 155px;

  padding: 5px;

  background: #ffffff;

  border: 1px solid #e2e8f0;

  border-radius: 8px;

  box-shadow:
    0 10px 30px
    rgba(15, 23, 42, .12);
}

.dropdown-menu button {
  width: 100%;
  height: 32px;

  display: flex;
  align-items: center;

  gap: 7px;

  padding: 0 9px;

  border: none;

  border-radius: 5px;

  background: transparent;

  color: #475569;

  font-size: 10px;

  text-align: left;

  cursor: pointer;
}

.dropdown-menu button:hover {
  background: #f8fafc;
}

.dropdown-menu button:disabled {
  opacity: .55;
  cursor: not-allowed;
}

.dropdown-menu .delete-option {
  color: #dc2626;
}

/* STATUS */

.status-row {
  display: flex;
  align-items: center;

  gap: 7px;

  margin-top: 10px;
}

.project-status {
  min-height: 22px;

  display: inline-flex;
  align-items: center;

  gap: 5px;

  padding: 0 8px;

  border-radius: 20px;

  border: 1px solid;

  font-size: 8px;
  font-weight: 800;

  text-transform: uppercase;
}

.status-open {
  color: #15803d;
  background: #f0fdf4;
  border-color: #86efac;
}

.status-progress {
  color: #b45309;
  background: #fffbeb;
  border-color: #fcd34d;
}

.status-completed {
  color: #2563eb;
  background: #eff6ff;
  border-color: #93c5fd;
}

.status-cancelled {
  color: #dc2626;
  background: #fef2f2;
  border-color: #fca5a5;
}

.status-draft {
  color: #64748b;
  background: #f8fafc;
  border-color: #cbd5e1;
}

.project-type-badge {
  display: inline-flex;
  align-items: center;

  min-height: 22px;

  padding: 0 8px;

  border-radius: 20px;

  background: #f8fafc;

  border: 1px solid #e2e8f0;

  color: #64748b;

  font-size: 8px;
  font-weight: 700;
}

/* DESCRIPTION */

.project-description {
  min-height: 47px;

  margin: 11px 0;

  color: #64748b;

  font-size: 10px;

  line-height: 1.55;

  display: -webkit-box;

  -webkit-line-clamp: 3;

  -webkit-box-orient: vertical;

  overflow: hidden;
}

/* ============================================================
   ⭐ TECHNOLOGY / SKILLS
============================================================ */

.technology-box {
  padding: 10px;

  border-radius: 9px;

  background: #f8fafc;

  border: 1px solid #eef2f7;
}

.technology-box > span {
  display: block;

  margin-bottom: 7px;

  color: #94a3b8;

  font-size: 7px;

  font-weight: 700;

  letter-spacing: .3px;
}

.skills-list {
  display: flex;

  flex-wrap: wrap;

  gap: 5px;
}

.skill-badge {
  display: inline-flex;

  align-items: center;

  min-height: 22px;

  padding: 0 8px;

  border-radius: 5px;

  background: #ffffff;

  border: 1px solid #dbeafe;

  color: #2563eb;

  font-size: 8px;

  font-weight: 700;

  line-height: 1.2;

  white-space: nowrap;
}

.skill-badge:hover {
  background: #eff6ff;
}

.no-skills {
  display: block;

  color: #64748b;

  font-size: 9px;
}

/* DETAILS */

.project-details {
  display: grid;

  grid-template-columns:
    1fr 1fr;

  gap: 10px;

  margin: 14px 0;
}

.project-detail {
  display: flex;

  align-items: flex-start;

  gap: 7px;

  min-width: 0;

  color: #64748b;
}

.project-detail svg {
  flex-shrink: 0;

  margin-top: 1px;

  color: #2563eb;
}

.project-detail div {
  min-width: 0;
}

.project-detail span {
  display: block;

  margin-bottom: 2px;

  color: #94a3b8;

  font-size: 7px;
}

.project-detail strong {
  display: block;

  color: #475569;

  font-size: 9px;

  line-height: 1.35;

  word-break: break-word;
}

/* EXTRA */

.extra-info {
  display: grid;

  grid-template-columns:
    repeat(3, 1fr);

  gap: 6px;

  padding: 9px 0;

  border-top: 1px solid #f1f5f9;
}

.extra-info > div {
  min-width: 0;
}

.extra-info span {
  display: block;

  margin-bottom: 3px;

  color: #94a3b8;

  font-size: 7px;
}

.extra-info strong {
  display: block;

  color: #475569;

  font-size: 9px;

  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;
}

/* FOOTER */

.card-footer {
  display: flex;

  gap: 7px;

  margin-top: auto;

  padding-top: 12px;

  border-top: 1px solid #f1f5f9;
}

.view-btn,
.applications-btn {
  height: 32px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  gap: 5px;

  border-radius: 6px;

  font-size: 9px;

  font-weight: 700;

  cursor: pointer;
}

.view-btn {
  flex: 1;

  border: 1px solid #dbe3ef;

  background: #ffffff;

  color: #2563eb;
}

.view-btn:hover {
  background: #eff6ff;
}

.applications-btn {
  flex: 1;

  border: none;

  background: #2563eb;

  color: #ffffff;
}

.applications-btn:hover {
  background: #1d4ed8;
}

/* LOADING */

.loading-state {
  min-height: 350px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  text-align: center;

  color: #64748b;
}

.loading-state h3 {
  margin: 12px 0 4px;

  color: #334155;

  font-size: 17px;
}

.loading-state p {
  margin: 0;

  color: #94a3b8;

  font-size: 11px;
}

/* EMPTY */

.empty-state {
  min-height: 350px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  text-align: center;
}

.empty-icon {
  width: 66px;
  height: 66px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 50%;

  background: #eff6ff;

  color: #2563eb;
}

.empty-state h3 {
  margin: 13px 0 5px;

  color: #334155;

  font-size: 17px;
}

.empty-state p {
  max-width: 430px;

  margin: 0 0 14px;

  color: #94a3b8;

  font-size: 11px;
}

.empty-state button {
  height: 34px;

  display: inline-flex;

  align-items: center;

  gap: 6px;

  padding: 0 14px;

  border: none;

  border-radius: 7px;

  background: #2563eb;

  color: #ffffff;

  font-size: 10px;

  font-weight: 700;

  cursor: pointer;
}

/* MODAL */

.modal-overlay {
  position: fixed;

  inset: 0;

  z-index: 1000;

  display: flex;

  align-items: center;

  justify-content: center;

  padding: 20px;

  background:
    rgba(15, 23, 42, .48);

  backdrop-filter: blur(4px);
}

.project-modal {
  width: min(700px, 100%);

  max-height: 90vh;

  display: flex;

  flex-direction: column;

  overflow: hidden;

  background: #ffffff;

  border-radius: 15px;

  box-shadow:
    0 25px 70px
    rgba(15, 23, 42, .25);
}

.modal-header {
  display: flex;

  align-items: flex-start;

  justify-content: space-between;

  gap: 15px;

  padding: 18px 20px;

  border-bottom: 1px solid #e2e8f0;
}

.modal-title {
  display: flex;

  align-items: center;

  gap: 11px;

  min-width: 0;
}

.modal-icon {
  width: 40px;
  height: 40px;

  display: flex;

  align-items: center;

  justify-content: center;

  flex-shrink: 0;

  border-radius: 9px;

  background: #eff6ff;

  color: #2563eb;
}

.modal-title h2 {
  margin: 0;

  color: #0f172a;

  font-size: 17px;

  line-height: 1.35;
}

.modal-title span {
  display: block;

  margin-top: 3px;

  color: #94a3b8;

  font-size: 9px;
}

.modal-close {
  width: 32px;
  height: 32px;

  display: flex;

  align-items: center;

  justify-content: center;

  flex-shrink: 0;

  border: none;

  border-radius: 7px;

  background: #f8fafc;

  color: #64748b;

  cursor: pointer;
}

.modal-close:hover {
  background: #f1f5f9;

  color: #334155;
}

.modal-body {
  overflow-y: auto;

  padding: 20px;
}

.modal-status-row {
  display: flex;

  align-items: center;

  gap: 8px;

  margin-bottom: 18px;
}

.modal-section {
  margin-bottom: 18px;
}

.modal-section h4 {
  margin: 0 0 7px;

  color: #334155;

  font-size: 11px;

  font-weight: 800;

  text-transform: uppercase;

  letter-spacing: .3px;
}

.modal-section p {
  margin: 0;

  color: #64748b;

  font-size: 11px;

  line-height: 1.65;
}

/* ============================================================
   ⭐ MODAL SKILLS
============================================================ */

.modal-skills {
  display: flex;

  flex-wrap: wrap;

  gap: 7px;

  padding: 12px;

  border-radius: 9px;

  background: #f8fafc;

  border: 1px solid #eef2f7;
}

.modal-skill-badge {
  display: inline-flex;

  align-items: center;

  min-height: 26px;

  padding: 0 10px;

  border-radius: 6px;

  background: #ffffff;

  border: 1px solid #bfdbfe;

  color: #2563eb;

  font-size: 9px;

  font-weight: 700;

  box-shadow:
    0 1px 2px
    rgba(15, 23, 42, .03);
}

.modal-tech {
  padding: 11px;

  border-radius: 8px;

  background: #f8fafc;

  color: #64748b;

  font-size: 10px;

  line-height: 1.5;
}

/* INFO */

.modal-info-grid {
  display: grid;

  grid-template-columns:
    1fr 1fr;

  gap: 10px;

  margin-bottom: 18px;
}

.modal-info-item {
  display: flex;

  align-items: flex-start;

  gap: 8px;

  padding: 11px;

  border: 1px solid #edf2f7;

  border-radius: 9px;

  background: #ffffff;
}

.modal-info-item svg {
  flex-shrink: 0;

  margin-top: 1px;

  color: #2563eb;
}

.modal-info-item span {
  display: block;

  margin-bottom: 3px;

  color: #94a3b8;

  font-size: 8px;
}

.modal-info-item strong {
  display: block;

  color: #475569;

  font-size: 10px;

  word-break: break-word;
}

/* CLIENT */

.client-box {
  display: flex;

  align-items: center;

  gap: 10px;

  padding: 12px;

  border-radius: 9px;

  background: #f8fafc;
}

.client-icon {
  width: 35px;
  height: 35px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 8px;

  background: #ffffff;

  color: #2563eb;
}

.client-box span {
  display: block;

  color: #94a3b8;

  font-size: 8px;

  margin-bottom: 2px;
}

.client-box strong {
  display: block;

  color: #334155;

  font-size: 10px;
}

.client-box small {
  display: block;

  margin-top: 2px;

  color: #94a3b8;

  font-size: 8px;
}

/* MODAL FOOTER */

.modal-footer {
  display: flex;

  justify-content: flex-end;

  gap: 8px;

  padding: 14px 20px;

  border-top: 1px solid #e2e8f0;
}

.modal-secondary-btn,
.modal-primary-btn {
  height: 34px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  gap: 6px;

  padding: 0 13px;

  border-radius: 7px;

  font-size: 10px;

  font-weight: 700;

  cursor: pointer;
}

.modal-secondary-btn {
  border: 1px solid #dbe3ef;

  background: #ffffff;

  color: #64748b;
}

.modal-primary-btn {
  border: none;

  background: #2563eb;

  color: #ffffff;
}

.modal-primary-btn:hover {
  background: #1d4ed8;
}

/* SPIN */

.spin {
  animation:
    freelanceProjectSpin
    1s linear infinite;
}

@keyframes freelanceProjectSpin {

  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }

}

/* RESPONSIVE */

@media (max-width: 1150px) {

  .project-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .stats-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

}

@media (max-width: 750px) {

  .freelance-page {
    padding: 15px;
  }

  .freelance-header {
    align-items: flex-start;

    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .refresh-btn,
  .post-project-btn {
    flex: 1;
  }

  .project-toolbar {
    align-items: stretch;

    flex-direction: column;
  }

  .search-container {
    max-width: none;
  }

  .filter-container {
    width: 100%;

    box-sizing: border-box;
  }

  .filter-container select {
    flex: 1;
  }

  .project-grid {
    grid-template-columns: 1fr;
  }

}

@media (max-width: 500px) {

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .header-actions {
    flex-direction: column;
  }

  .refresh-btn,
  .post-project-btn {
    width: 100%;
  }

  .project-details {
    grid-template-columns: 1fr;
  }

  .extra-info {
    grid-template-columns:
      1fr 1fr 1fr;
  }

  .modal-overlay {
    padding: 10px;
  }

  .project-modal {
    max-height: 95vh;
  }

  .modal-info-grid {
    grid-template-columns: 1fr;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-secondary-btn,
  .modal-primary-btn {
    width: 100%;
  }

}

`;

export default FreelanceProjects;